import { removeFromLocalStorage } from '@/lib/local-storage/local-storage';
import type { ErrorResponse } from '@/requests/types';
import { datadogRum } from '@datadog/browser-rum';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { CurrentUser } from './types';
import { getCurrentUser } from '@/requests/getCurrentUser';
import { useSessionValues } from './hooks/use-session-values';
import { SESSION_KEY } from './constants';

export const USER_QUERY_KEY = 'current-user';

export const useGetCurrentUser = (
  options?: Omit<
    UseQueryOptions<unknown, ErrorResponse, CurrentUser, string[]>,
    'queryKey' | 'queryFn' | 'staleTime' | 'refetchOnWindowFocus'
  >
) => {
  const { customerId } = useSessionValues();

  const user = useQuery({
    queryKey: [USER_QUERY_KEY],
    queryFn: getCurrentUser,
    // 3 minutes
    staleTime: 60 * 3 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
    retryOnMount: true,
    ...options,
  });

  useEffect(() => {
    if (user.error?.status === 401) {
      removeFromLocalStorage({ key: SESSION_KEY });
    }
  }, [user.error?.status]);

  useEffect(() => {
    if (user.data) {
      const id = `${customerId}_${user.data?.id}`;
      datadogRum.setUser({
        id,
        name: user.data?.username,
        px_customer_id: customerId,
        px_user_id: user.data?.id,
      });
    }
  }, [customerId, user.data]);

  return user;
};
